import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDistanceToNow } from 'date-fns';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import RFPUploadModal from '../components/rfp/RFPUploadModal';
import DocumentSelectionModal from '../components/rfp/DocumentSelectionModal';
import ContextDocumentSelector from '../components/rfp/ContextDocumentSelector';
import DownloadResearchModal from '../components/rfp/DownloadResearchModal';
import { apiClient } from '../services/apiClient';

// Helper function to parse date strings without timezone issues
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('T')[0].split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const RFPResearchPage = () => {
  const navigate = useNavigate();
  const [researchList, setResearchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDocSelectionModal, setShowDocSelectionModal] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Upload workflow state
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState(null);
  const [pendingResearchId, setPendingResearchId] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null); // Track selected AI model

  useEffect(() => {
    loadResearch();
  }, [statusFilter]);

  const loadResearch = async () => {
    try {
      setLoading(true);
      setError('');
      const filters = statusFilter ? { status: statusFilter } : {};
      const response = await apiClient.listRFPResearch(filters);

      if (response.success) {
        setResearchList(response.data.research || []);
      }
    } catch (err) {
      setError('Failed to load RFP research entries');
      console.error('Error loading research:', err);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Handle file upload
  const handleUploadComplete = async (file, sourceUrl, model) => {
    try {
      // Store the selected model for later use
      setSelectedModel(model);

      const response = await apiClient.uploadRFPFile(file, sourceUrl);

      if (response.success) {
        setUploadResult(response.data);

        if (response.data.requiresUserSelection) {
          // ZIP file - show document selection modal
          setShowUploadModal(false);
          setShowDocSelectionModal(true);
        } else {
          // Single file - proceed directly
          await handleDocumentSelection(response.data.documents);
        }
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Upload failed');
    }
  };

  // Step 2: Handle document selection (for ZIP files)
  const handleDocumentSelection = async (documents) => {
    try {
      setSelectedDocs(documents);
      setShowDocSelectionModal(false);

      // Create research entry
      const response = await apiClient.createRFPResearch(
        documents,
        uploadResult?.sourceUrl || null
      );

      if (response.success) {
        setPendingResearchId(response.data.researchId);
        setShowContextModal(true);
      }
    } catch (err) {
      setError('Failed to create research entry');
      console.error('Error creating research:', err);
    }
  };

  // Step 3: Handle context document selection and start analysis
  const handleContextSelection = async (contextDocIds) => {
    try {
      setShowContextModal(false);

      // Navigate to detail page which will trigger analysis, passing the selected model
      navigate(`/rfp-research/${pendingResearchId}`, {
        state: { contextDocIds, startAnalysis: true, selectedModel }
      });
    } catch (err) {
      setError('Failed to start analysis');
      console.error('Error starting analysis:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      analyzing: 'bg-blue-100 text-blue-800 border-blue-200',
      analyzed: 'bg-gray-100 text-gray-800 border-gray-200',
      bid: 'bg-green-100 text-green-800 border-green-200',
      no_bid: 'bg-red-100 text-red-800 border-red-200',
      converted: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    const labels = {
      analyzing: 'Analyzing',
      analyzed: 'Analyzed',
      bid: 'Bid',
      no_bid: 'No Bid',
      converted: 'Converted'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${badges[status] || badges.analyzed}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getRecommendationBadge = (recommendation) => {
    const badges = {
      bid: 'bg-green-500 text-white',
      no_bid: 'bg-red-500 text-white',
      maybe: 'bg-amber-500 text-white'
    };
    const icons = {
      bid: 'check-circle',
      no_bid: 'times-circle',
      maybe: 'question-circle'
    };
    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full ${badges[recommendation] || badges.maybe}`}>
        <FontAwesomeIcon icon={icons[recommendation] || 'question-circle'} className="mr-2" />
        <span className="font-medium capitalize">{recommendation?.replace('_', ' ')}</span>
      </div>
    );
  };

  const handleDelete = async (e, researchId) => {
    e.stopPropagation(); // Prevent card click navigation

    if (!window.confirm('Are you sure you want to delete this research entry? This cannot be undone.')) {
      return;
    }

    try {
      await apiClient.deleteRFPResearch(researchId);
      // Reload the list
      await loadResearch();
    } catch (err) {
      setError('Failed to delete research entry');
      console.error('Delete error:', err);
    }
  };

  const handleDownloadReport = async (researchIds) => {
    try {
      await apiClient.downloadRFPResearchReport(researchIds);
    } catch (err) {
      setError('Failed to generate report');
      console.error('Download error:', err);
      throw err;
    }
  };

  // Filter research list based on search query
  const filteredResearch = researchList.filter(item => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      item.rfp_title?.toLowerCase().includes(query) ||
      item.customer_name?.toLowerCase().includes(query) ||
      item.created_by_name?.toLowerCase().includes(query) ||
      item.bid_recommendation?.toLowerCase().includes(query)
    );
  });

  return (
    <Layout title="RFP Research">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">RFP Research</h1>
              <p className="text-slate-600 mt-1">
                Upload RFPs for AI-powered analysis and bid/no-bid recommendations
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadResearch}
                className="text-primary-600 hover:text-primary-800 transition-colors flex items-center"
              >
                <FontAwesomeIcon icon="sync-alt" className="mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 shadow-sm"
              >
                <FontAwesomeIcon icon="plus" />
                <span>New Analysis</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <FontAwesomeIcon
                icon="search"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search RFPs by title, customer, creator, or recommendation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-10 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  title="Clear search"
                >
                  <FontAwesomeIcon icon="times" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowDownloadModal(true)}
              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 whitespace-nowrap"
              disabled={researchList.filter(r => r.status === 'analyzed').length === 0}
            >
              <FontAwesomeIcon icon="download" />
              <span>Download Report</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <ErrorMessage message={error} />}

        {/* Research List */}
        {loading ? (
          <div className="py-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-slate-600">Loading research entries...</p>
          </div>
        ) : researchList.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <FontAwesomeIcon icon="search" className="text-6xl text-slate-300 mb-4" />
            <h3 className="text-xl font-medium text-slate-900 mb-2">No RFP Research Yet</h3>
            <p className="text-slate-600 mb-6">
              Upload an RFP document to get started with AI-powered analysis
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center space-x-2"
            >
              <FontAwesomeIcon icon="upload" />
              <span>Upload RFP</span>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              const analyzedResearch = filteredResearch.filter(r => r.status === 'analyzed').sort((a, b) => {
                if (!a.due_date && !b.due_date) return 0;
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date) - new Date(b.due_date);
              });
              const convertedResearch = filteredResearch.filter(r => r.status === 'converted').sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
              );
              const noBidResearch = filteredResearch.filter(r => r.status === 'no_bid').sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
              );

              return (
                <>
                  <ResearchTable
                    title="Analyzed RFPs"
                    subtitle="Awaiting bid/no-bid decision"
                    research={analyzedResearch}
                    navigate={navigate}
                    handleDelete={handleDelete}
                    getRecommendationBadge={getRecommendationBadge}
                    emptyMessage="No analyzed RFPs"
                    searchActive={!!searchQuery.trim()}
                    hasSearchResults={analyzedResearch.length > 0}
                  />

                  <ResearchTable
                    title="Converted to Proposals"
                    subtitle="RFPs that have been converted to active proposals"
                    research={convertedResearch}
                    navigate={navigate}
                    handleDelete={handleDelete}
                    getRecommendationBadge={getRecommendationBadge}
                    emptyMessage="No converted RFPs"
                    collapsible={true}
                    searchActive={!!searchQuery.trim()}
                    hasSearchResults={convertedResearch.length > 0}
                  />

                  <ResearchTable
                    title="No Bid (Archived)"
                    subtitle="RFPs that were declined"
                    research={noBidResearch}
                    navigate={navigate}
                    handleDelete={handleDelete}
                    getRecommendationBadge={getRecommendationBadge}
                    emptyMessage="No archived RFPs"
                    collapsible={true}
                    searchActive={!!searchQuery.trim()}
                    hasSearchResults={noBidResearch.length > 0}
                  />
                </>
              );
            })()}
          </div>
        )}

        {/* Modals */}
        <RFPUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleUploadComplete}
        />

        <DocumentSelectionModal
          isOpen={showDocSelectionModal}
          onClose={() => setShowDocSelectionModal(false)}
          documents={uploadResult?.documents || []}
          onConfirm={handleDocumentSelection}
        />

        <ContextDocumentSelector
          isOpen={showContextModal}
          onClose={() => setShowContextModal(false)}
          onConfirm={handleContextSelection}
        />

        <DownloadResearchModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          analyzedResearch={researchList.filter(r => r.status === 'analyzed').sort((a, b) => {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
          })}
          onDownload={handleDownloadReport}
        />
      </div>
    </Layout>
  );
};

// Research Table Component
const ResearchTable = ({
  title,
  subtitle,
  research,
  navigate,
  handleDelete,
  getRecommendationBadge,
  emptyMessage,
  collapsible = false,
  hasSearchResults = false,
  searchActive = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible);
  const [wasCollapsedBeforeSearch, setWasCollapsedBeforeSearch] = useState(collapsible);

  // Expand if there are search results, collapse when search is cleared
  useEffect(() => {
    if (searchActive && hasSearchResults && collapsible) {
      // Save current state before expanding due to search
      if (isCollapsed) {
        setWasCollapsedBeforeSearch(true);
      }
      setIsCollapsed(false);
    } else if (!searchActive && collapsible) {
      // Restore previous state when search is cleared
      setIsCollapsed(wasCollapsedBeforeSearch);
    }
  }, [searchActive, hasSearchResults, collapsible]);

  if (research.length === 0) {
    return null; // Don't show empty tables
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div
        className={`px-6 py-4 border-b border-slate-200 ${collapsible ? 'cursor-pointer hover:bg-slate-50' : ''}`}
        onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center">
              {title}
              <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-600 text-sm rounded-full">
                {research.length}
              </span>
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>
          </div>
          {collapsible && (
            <FontAwesomeIcon
              icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
              className="text-slate-400"
            />
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  RFP Title / Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Recommendation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Match Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Response Difficulty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {research.map((item) => (
                <tr
                  key={item.research_id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/rfp-research/${item.research_id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.due_date ? (
                      <div className="flex items-center text-sm text-slate-900">
                        <FontAwesomeIcon icon="calendar" className="mr-2 text-slate-400" />
                        {parseLocalDate(item.due_date).toLocaleDateString()}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-slate-900">
                        {item.rfp_title}
                      </span>
                      {item.customer_name && (
                        <span className="text-xs text-slate-500 flex items-center mt-1">
                          <FontAwesomeIcon icon="building" className="mr-1" />
                          {item.customer_name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {item.bid_recommendation ? (
                      getRecommendationBadge(item.bid_recommendation)
                    ) : (
                      <span className="text-xs text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {item.recommendation_score ? (
                      <div className="w-32">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-600">Score</span>
                          <span className="font-medium text-slate-900">{item.recommendation_score}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              item.recommendation_score >= 80 ? 'bg-green-500' :
                              item.recommendation_score >= 60 ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${item.recommendation_score}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.response_difficulty_score ? (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.response_difficulty_score <= 3 ? 'bg-green-100 text-green-800' :
                          item.response_difficulty_score <= 6 ? 'bg-yellow-100 text-yellow-800' :
                          item.response_difficulty_score <= 8 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.response_difficulty_score}/10
                        </span>
                        <span className="text-xs text-slate-600">
                          {item.response_difficulty_score <= 3 ? 'Simple' :
                           item.response_difficulty_score <= 6 ? 'Moderate' :
                           item.response_difficulty_score <= 8 ? 'Complex' :
                           'Very Complex'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.estimated_value ? (
                      <div className="flex items-center text-sm text-slate-900">
                        <FontAwesomeIcon icon="dollar-sign" className="mr-1 text-slate-400" />
                        ${(item.estimated_value / 1000000).toFixed(1)}M
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                    {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-slate-700">
                      <FontAwesomeIcon icon="user" className="mr-2 text-slate-400 text-xs" />
                      {item.created_by_name || 'Unknown'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => handleDelete(e, item.research_id)}
                      className="text-red-400 hover:text-red-600 p-2 rounded transition-colors"
                      title="Delete research"
                    >
                      <FontAwesomeIcon icon="trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RFPResearchPage;
